// Intake Intelligence duck slice, extracted from DataEntryDetailsDucks.ts
// (Spec #34098 / F1a, AC17 - tidy-first structural refactor #5).
//
// Extraction changes only file location, not state shape, action-type
// strings, or action-creator signatures (Business Rule 18):
//   - state.dataEntryState.requestAnalysis / getIntakeIntelligenceStatus /
//     requestIntakeIntelligenceStatus / intakeFeedbackSubmitStatus keep
//     their existing flat shape - intakeDucksReducer is a DELEGATING
//     reducer called from DataEntryDetailsDucks.ts's main `dataEntryState`
//     reducer's `default` case, not a combineReducers composition (which
//     would nest state under a new key and break every existing direct
//     state read across the app).
//   - Action-type string VALUES are unchanged (IntakeActionConstants below
//     mirrors the literal strings already used by DataEntryActionConstants
//     in DataEntryDetailsDucks.ts, so takeLatest registrations and any
//     existing `DataEntryActionConstants.getIntakeIntelligence*` reference
//     keep matching the same dispatched actions).

import { call, put } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { API_INITIAL, API_FETCHING, API_SUCCESS, API_FAILURE, ReduxApiStatus } from "../../common/constants/ApiConstants";
import { IntakeFromApiResponse, RequestAnalysis, RequestAnalysisData } from "../../models/intake";
import {
    getIntakeIntelligenceStatusApi,
    requestIntakeIntelligenceApi,
    intakeIntelligenceFeedbackApi,
} from "./test/DataEntry.APIs";

/** API_ERROR_STATE lives in DataEntryDetailsDucks.ts's DataEntryActionConstants; duplicated here (same literal) to avoid a circular import. */
const API_ERROR_STATE = "API_ERROR_STATE";

export const IntakeActionConstants = {
    getIntakeIntelligenceRequest: "getIntakeIntelligenceRequest",
    getIntakeIntelligenceSuccess: "getIntakeIntelligenceSuccess",
    getIntakeIntelligenceFailure: "getIntakeIntelligenceFailure",
    requestIntakeIntelligenceRequest: "requestIntakeIntelligenceRequest",
    requestIntakeIntelligenceSuccess: "requestIntakeIntelligenceSuccess",
    requestIntakeIntelligenceFailure: "requestIntakeIntelligenceFailure",
    feedbackForIntakeFieldRequest: "feedbackForIntakeFieldRequest",
    feedbackForIntakeFieldSuccess: "feedbackForIntakeFieldSuccess",
    feedbackForIntakeFieldFailure: "feedbackForIntakeFieldFailure",
    // Spec #34100 / F1b-rois (AC2): clear the prior analysis before an
    // auto re-extract when the request document is replaced.
    resetIntakeIntelligence: "resetIntakeIntelligence",
} as const;

export type IntakeFeedbackApiStatus = {
    readonly [key in keyof RequestAnalysisData]?: ReduxApiStatus;
};

export interface IntakeDucksState {
    readonly requestAnalysis?: RequestAnalysis;
    readonly getIntakeIntelligenceStatus: ReduxApiStatus;
    readonly requestIntakeIntelligenceStatus: ReduxApiStatus;
    readonly intakeFeedbackSubmitStatus: IntakeFeedbackApiStatus;
}

export const intakeDucksInitialState: IntakeDucksState = {
    requestAnalysis: undefined,
    getIntakeIntelligenceStatus: API_INITIAL,
    requestIntakeIntelligenceStatus: API_INITIAL,
    intakeFeedbackSubmitStatus: {},
};

export interface IntakeIntelligenceRequestAction {
    type: typeof IntakeActionConstants.getIntakeIntelligenceRequest |
        typeof IntakeActionConstants.requestIntakeIntelligenceRequest;
    organizationId: number;
    requestBufferId: number;
    onSuccess?: () => void;
}

export interface IntakeFeedback {
    facilityId: number;
    requestBufferId: number;
    feedbackItem: keyof RequestAnalysisData;
    feedback: boolean | null;
}

export interface SetFeedbackForIntakeFieldAction {
    type: typeof IntakeActionConstants.feedbackForIntakeFieldRequest;
    data: IntakeFeedback;
}

/**
 * Load existing intake intelligence data if it exists.
 */
export function getIntakeIntelligenceActionCreator(
    organizationId: number,
    requestBufferId: number,
    onSuccess?: () => void,
): IntakeIntelligenceRequestAction {
    return {
        type: IntakeActionConstants.getIntakeIntelligenceRequest,
        organizationId,
        requestBufferId,
        onSuccess,
    };
}

/**
 * Get intake intelligence data, generating it if necessary.
 */
export function requestIntakeIntelligenceActionCreator(
    organizationId: number,
    requestBufferId: number,
    onSuccess?: () => void,
): IntakeIntelligenceRequestAction {
    return {
        type: IntakeActionConstants.requestIntakeIntelligenceRequest,
        organizationId,
        requestBufferId,
        onSuccess,
    };
}

export function setFeedbackForIntakeFieldActionCreator(feedback: IntakeFeedback): SetFeedbackForIntakeFieldAction {
    return {
        type: IntakeActionConstants.feedbackForIntakeFieldRequest,
        data: feedback,
    };
}

export interface ResetIntakeIntelligenceAction {
    type: typeof IntakeActionConstants.resetIntakeIntelligence;
}

/**
 * Clears the prior analysis from the store (Spec #34100 / F1b-rois, AC2).
 * Dispatched before an auto re-extract when the request document is
 * replaced, so stale confidence/grounding disappears immediately rather
 * than lingering until the fresh extraction completes. Read-time/transient
 * only - touches no persisted state (AC11).
 */
export function resetIntakeIntelligenceActionCreator(): ResetIntakeIntelligenceAction {
    return {
        type: IntakeActionConstants.resetIntakeIntelligence,
    };
}

/**
 * Delegating reducer for the 9 Intake Intelligence action types. Called from
 * DataEntryDetailsDucks.ts's main `dataEntryState` reducer's `default` case -
 * returns `state` unchanged for any action type it doesn't own, so it is
 * always safe to call unconditionally from that `default` branch.
 */
export function intakeDucksReducer<S extends IntakeDucksState>(state: S, action: any): S {
    switch (action.type) {
        case IntakeActionConstants.getIntakeIntelligenceRequest:
            return {
                ...state,
                getIntakeIntelligenceStatus: API_FETCHING,
            } as S;
        case IntakeActionConstants.getIntakeIntelligenceSuccess:
            return {
                ...state,
                getIntakeIntelligenceStatus: API_SUCCESS,
                requestAnalysis: action.data,
            } as S;
        case IntakeActionConstants.getIntakeIntelligenceFailure:
            return {
                ...state,
                getIntakeIntelligenceStatus: API_FAILURE,
            } as S;
        case IntakeActionConstants.requestIntakeIntelligenceRequest:
            return {
                ...state,
                requestIntakeIntelligenceStatus: API_FETCHING,
            } as S;
        case IntakeActionConstants.requestIntakeIntelligenceSuccess:
            return {
                ...state,
                requestIntakeIntelligenceStatus: API_SUCCESS,
                requestAnalysis: action.data,
            } as S;
        case IntakeActionConstants.requestIntakeIntelligenceFailure:
            return {
                ...state,
                requestIntakeIntelligenceStatus: API_FAILURE,
            } as S;
        case IntakeActionConstants.feedbackForIntakeFieldRequest: {
            const data: IntakeFeedback = action.data;
            return {
                ...state,
                intakeFeedbackSubmitStatus: {
                    ...state.intakeFeedbackSubmitStatus,
                    [data.feedbackItem]: API_FETCHING,
                },
            } as S;
        }
        case IntakeActionConstants.feedbackForIntakeFieldSuccess: {
            const data: IntakeFeedback = action.data;
            return {
                ...state,
                intakeFeedbackSubmitStatus: {
                    ...state.intakeFeedbackSubmitStatus,
                    [data.feedbackItem]: API_SUCCESS,
                },
                requestAnalysis: state.requestAnalysis && {
                    ...state.requestAnalysis,
                    dataApprovals: {
                        ...state.requestAnalysis.dataApprovals,
                        [data.feedbackItem]: data.feedback,
                    },
                },
            } as S;
        }
        case IntakeActionConstants.feedbackForIntakeFieldFailure: {
            const data: IntakeFeedback = action.data;
            return {
                ...state,
                intakeFeedbackSubmitStatus: {
                    ...state.intakeFeedbackSubmitStatus,
                    [data.feedbackItem]: API_FAILURE,
                },
            } as S;
        }
        case IntakeActionConstants.resetIntakeIntelligence:
            return {
                ...state,
                requestAnalysis: undefined,
                getIntakeIntelligenceStatus: API_INITIAL,
                requestIntakeIntelligenceStatus: API_INITIAL,
                intakeFeedbackSubmitStatus: {},
            } as S;
        default:
            return state;
    }
}

/**
 * Load existing intake intelligence data without generating any new data.
 * @param action
 */
export function* getIntakeIntelligenceSaga(action: IntakeIntelligenceRequestAction) {
    try {
        const response: AxiosResponse = yield call(
            getIntakeIntelligenceStatusApi,
            action.organizationId,
            action.requestBufferId,
        );
        yield put({
            type: IntakeActionConstants.getIntakeIntelligenceSuccess,
            data: IntakeFromApiResponse(response.data),
        });
        try {
            // Try/catch so this can't fail the whole saga
            action.onSuccess?.();
        } catch (err) {
            console.error(err);
        }
    } catch (err) {
        console.error(err);
        yield put({ type: IntakeActionConstants.getIntakeIntelligenceFailure });
        if (typeof err === "object" && "response" in err) {
            yield put({ type: API_ERROR_STATE, data: err.response });
        }
    }
}

/**
 * Load intake intelligence data, generating it if necessary.
 * @param action
 */
export function* requestIntakeIntelligenceSaga(action: IntakeIntelligenceRequestAction) {
    try {
        const response: AxiosResponse = yield call(
            requestIntakeIntelligenceApi,
            action.organizationId,
            action.requestBufferId,
        );
        yield put({
            type: IntakeActionConstants.requestIntakeIntelligenceSuccess,
            data: IntakeFromApiResponse(response.data),
        });
        try {
            // Try/catch so this can't fail the whole saga
            action.onSuccess?.();
        } catch (err) {
            console.error(err);
        }
    } catch (err) {
        console.error(err);
        yield put({ type: IntakeActionConstants.requestIntakeIntelligenceFailure });
        if (typeof err === "object" && "response" in err) {
            // Specific handling for intake timeout - print specific error message
            if (err.response.data.detail === "INTAKE_TIMEOUT")
                err.response.statusText = "The automated intake process could not complete within the expected time (100 seconds). Please proceed with manual data entry for this request.";

            yield put({ type: API_ERROR_STATE, data: err.response });
        }
    }
}

export function* setFeedbackForIntakeFieldSaga(action: SetFeedbackForIntakeFieldAction) {
    try {
        yield call(
            intakeIntelligenceFeedbackApi,
            action.data.facilityId,
            action.data.requestBufferId,
            action.data.feedbackItem,
            action.data.feedback,
        );
        yield put({
            type: IntakeActionConstants.feedbackForIntakeFieldSuccess,
            data: action.data,
        });
    } catch (err) {
        console.error(err);
        yield put({
            type: IntakeActionConstants.feedbackForIntakeFieldFailure,
            data: action.data,
        });

        if (typeof err === "object" && "response" in err) {
            yield put({ type: API_ERROR_STATE, data: err.response });
        }
    }
}
